import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection({ t }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success(t.contact.success);
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch {
            toast.error(t.contact.error);
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="kontakt" className="border-t border-white/5 px-5 sm:px-8 py-24 sm:py-28">
            <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-2">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                >
                    <p className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-glacier">
                        <span className="inline-block h-px w-10 bg-glacier/60" />
                        {t.contact.overline}
                    </p>
                    <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight">
                        {t.contact.title}
                    </h2>
                    <p className="mt-5 max-w-md text-base leading-relaxed text-fog">{t.contact.sub}</p>

                    <div className="mt-12 space-y-7">
                        <div className="flex items-center gap-4">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-glacier/25 text-glacier">
                                <Mail size={16} />
                            </span>
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fog">
                                    {t.contact.emailLabel}
                                </p>
                                <a
                                    data-testid="contact-direct-email"
                                    href="mailto:webryno.studio@gmail.com"
                                    className="text-base text-ivory transition-colors duration-300 hover:text-glacier"
                                >
                                    webryno.studio@gmail.com
                                </a>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-glacier/25 text-glacier">
                                <Phone size={16} />
                            </span>
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fog">
                                    {t.contact.phoneLabel}
                                </p>
                                <a
                                    data-testid="contact-direct-phone"
                                    href="tel:+421907517652"
                                    className="text-base text-ivory transition-colors duration-300 hover:text-glacier"
                                >
                                    +421 907 517 652
                                </a>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-glacier/25 text-glacier">
                                <MapPin size={16} />
                            </span>
                            <p className="text-base text-ivory">{t.contact.locationLabel}</p>
                        </div>
                    </div>
                </motion.div>

                <motion.form
                    data-testid="contact-form"
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                    className="rounded-lg border border-white/10 bg-coal p-8 sm:p-10"
                >
                    <div className="space-y-5">
                        <input
                            data-testid="contact-input-name"
                            type="text"
                            required
                            minLength={2}
                            value={form.name}
                            onChange={set("name")}
                            placeholder={t.contact.name}
                            className="w-full rounded-sm border border-white/10 bg-ink px-5 py-4 text-sm text-ivory placeholder:text-fog/60 outline-none transition-[border-color] duration-300 focus:border-glacier"
                        />
                        <input
                            data-testid="contact-input-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={set("email")}
                            placeholder={t.contact.email}
                            className="w-full rounded-sm border border-white/10 bg-ink px-5 py-4 text-sm text-ivory placeholder:text-fog/60 outline-none transition-[border-color] duration-300 focus:border-glacier"
                        />
                        <input
                            data-testid="contact-input-phone"
                            type="tel"
                            value={form.phone}
                            onChange={set("phone")}
                            placeholder={t.contact.phone}
                            className="w-full rounded-sm border border-white/10 bg-ink px-5 py-4 text-sm text-ivory placeholder:text-fog/60 outline-none transition-[border-color] duration-300 focus:border-glacier"
                        />
                        <textarea
                            data-testid="contact-input-message"
                            required
                            minLength={10}
                            rows={5}
                            value={form.message}
                            onChange={set("message")}
                            placeholder={t.contact.message}
                            className="w-full resize-none rounded-sm border border-white/10 bg-ink px-5 py-4 text-sm text-ivory placeholder:text-fog/60 outline-none transition-[border-color] duration-300 focus:border-glacier"
                        />
                        <button
                            data-testid="contact-form-submit-button"
                            type="submit"
                            disabled={sending}
                            className="group inline-flex w-full items-center justify-center gap-3 rounded-sm bg-glacier btn-shine px-8 py-4 font-display text-sm font-semibold uppercase tracking-widest text-ink transition-[background-color,opacity] duration-300 hover:bg-ivory hover:shadow-[0_0_28px_rgba(74,222,128,0.3)] disabled:opacity-60"
                        >
                            {sending ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                            {sending ? t.contact.sending : t.contact.submit}
                        </button>
                    </div>
                </motion.form>
            </div>
        </section>
    );
}
